import React, { forwardRef, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { axios } from 'api'

import { Box, Flex, Image, NameList, ResponsiveSVG } from 'ui/acro'
import ClassCountBadge from './ClassCountBadge'
import { CardSubtitle, CardTitle } from '../styles'

import heartSVG from '../../../../../assets/images/heart.svg'
import heartFilledSVG from '../../../../../assets/images/heartFilled.svg'
import { storeEventInLocalStorage } from 'gtm/pushEvents'
import { AlomovesIconSVG } from 'images/latest'
import SubCategoryChipList from './SubCategoryChipList'
import CoachesListComponent from './CoachesListComponent'

const HeartIconBox = styled(Box)`
  svg {
    path {
      &.heart-icon {
        fill: #3b3b3b99;
      }
    }
  }
`

const Main = styled(Flex)`
  .no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f2f2f2;
    aspect-ratio: 16/9;
  }
  .series-image {
    width: 100%;
  }
`

const ProductCard = forwardRef(
  (
    {
      carouselItem,
      id,
      classCount,
      workoutCount,
      instructors,
      primaryCategory,
      primarySubcategory,
      thumbnails,
      title,
      slug,
      url,
      introVideoThumbnail,
      thumbnailImage,
      coaches,
      alignBox,
      variantTitle,
      variantSubtitle,
      favoriteCallback,
      subcategory,
      coverPhoto,
      position,
      eventName,
      location,
      difficultyLevel,
      isFevIcon = true,
      subcategories,
      ...rest
    },
    ref
  ) => {
    const FinalclassCount = classCount || workoutCount
    const finalSubcategory = primarySubcategory || subcategory
    const isBundle = String(rest?.actableType).toLowerCase() === 'bundle'
    const seriesUrl =
      url || (isBundle ? `/bundles/${slug}` : `/series/${slug}/workouts`)
    const [favoritesId, setFavoritesId] = useState(
      rest?.planSubscriptions?.[0]?.id
    )

    const image =
      coverPhoto?.url ||
      (thumbnails && (thumbnails.medium || thumbnails.default)) ||
      (introVideoThumbnail && introVideoThumbnail) ||
      (thumbnailImage === null ? '' : thumbnailImage)

    const removeSeries = (id) => {
      setFavoritesId(null)
      axios
        .delete(`/plan_subscriptions/${id}`)
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(null)
            favoriteCallback?.()
          } else {
            setFavoritesId(id)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(id)
        })
    }

    const addSeries = (id) => {
      setFavoritesId(id)
      axios
        .post(`/plan_subscriptions`, { plan_id: id })
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(res?.data?.id || null)
            favoriteCallback?.()
          } else {
            setFavoritesId(null)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(null)
        })
    }

    const pushEvent = (e) => {
      if (!eventName) {
        return
      }
      const coachNames = coaches
        ?.map((item) => `${item.firstName} ${item.lastName}`)
        .join(', ')

      const event_data = {
        event: eventName,
        position,
        instructor: coachNames,
        series: title,
        ...(location && { location })
      }
      storeEventInLocalStorage(event_data)
    }

    return (
      <Main
        ref={ref}
        flexDirection='column'
        alignItems={alignBox}
        position='relative'
        className={carouselItem ? 'swiper-slide unstyled' : 'unstyled'}
        as='a'
        onClick={pushEvent}
        href={seriesUrl}
        style={{
          minWidth: carouselItem ? '212px' : '173px'
        }}
      >
        {primaryCategory && isFevIcon && (
          <HeartIconBox
            zIndex={'1'}
            right={['5px', '6px', '6px', '4px']}
            top={['5px', '7px', '7px', '4px']}
            position={'absolute'}
            width={'32px'}
            height={'32px'}
            display={'flex'}
            borderRadius={'50%'}
            alignItems={'center'}
            justifyContent={'center'}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              !favoritesId ? addSeries(id) : removeSeries(favoritesId)
            }}
            backgroundColor={'#FFFFFF99'}
          >
            <ResponsiveSVG
              SVG={favoritesId ? heartFilledSVG : heartSVG}
              style={{
                cursor: 'pointer'
              }}
              width='24px'
              height='24px'
            />
          </HeartIconBox>
        )}
        {image ? (
          <Box position='relative' mb={['8px', '10px']}>
            <Image
              src={image}
              objectFit='cover'
              position='absolute'
              aspectRatio='16/9'
              top='0'
              left='0'
              style={{ aspectRatio: '16/9' }}
              className='series-image'
            />
            {FinalclassCount > 0 && (
              <ClassCountBadge
                bottom='16px !important'
                left='16px !important'
              >{`${FinalclassCount} Class${
                FinalclassCount <= 1 ? '' : 'es'
              }`}</ClassCountBadge>
            )}
          </Box>
        ) : (
          <Box className='no-image' mb={['8px', '10px']}>
            <ResponsiveSVG
              width={['68px', '68px', '130px', '130px']}
              height={['68px', '68px', '130px', '130px']}
              SVG={AlomovesIconSVG}
              className='no-thumbnail'
            />
          </Box>
        )}
        {title && (
          <CardTitle
            id='SeriesTitle'
            as='div'
            paddingBottom='6px'
            variant={variantTitle}
          >
            {title}
          </CardTitle>
        )}
        {instructors && (
          <CardSubtitle as='div'>
            <NameList people={instructors || coaches} />
          </CardSubtitle>
        )}
        {coaches && <CoachesListComponent coaches={coaches} />}

        <CardSubtitle as='div' marginTop='4px'>
          {difficultyLevel && `${difficultyLevel}`}
        </CardSubtitle>
        {subcategories && <SubCategoryChipList subcategories={subcategories} />}
      </Main>
    )
  }
)

ProductCard.propTypes = {
  duration: PropTypes.string,
  instructors: PropTypes.array,
  id: PropTypes.string,
  primaryCategory: PropTypes.object,
  primarySubcategory: PropTypes.object,
  title: PropTypes.string.isRequired,
  thumbnails: PropTypes.object,
  coverPhoto: PropTypes.array,
  coaches: PropTypes.array,
  alignBox: PropTypes.string,
  variantSubtitle: PropTypes.string,
  variantTitle: PropTypes.string,
  rest: PropTypes.object
}

export default ProductCard
